import { useState, useRef, useEffect, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, Check, Loader2 } from 'lucide-react'
import { get, post } from '@/lib/api'
import { getAuthState, tryRestoreSession } from '@/lib/auth'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { KindBadge, announceKind, toAnnouncement, type AnnouncementContent } from '@/components/announce/AnnouncementView'
import { AnnouncementSheet, refreshNotifications } from '@/components/announce/AnnouncementSheet'

export interface NotificationItem {
  id: string
  userId: string
  shopId?: string | null
  type: string
  title: string
  message: string
  data?: Record<string, any> | null
  isRead: boolean
  readAt?: string | null
  createdAt: string
}

/** Phần một dòng cần để hiện trong chuông — chung cho chuông đơn vị và chuông khu /npp (hộp thư quản trị). */
export type BellItem = Pick<NotificationItem, 'id' | 'type' | 'title' | 'message' | 'data' | 'isRead' | 'createdAt'>

const ANNOUNCE_DOT = { warning: 'bg-danger', promo: 'bg-ok', info: 'bg-info' } as const

/** Mức ý nghĩa của một loại thông báo — hiện bằng chấm, không bằng icon. */
function getNotificationTone(item: BellItem): string {
  switch (item.type) {
    case 'announcement':
      return ANNOUNCE_DOT[announceKind(item.data?.kind)]
    case 'order_delivered':
    case 'reconcile_ready':
      return 'bg-ok'
    case 'order_failed':
      return 'bg-danger'
    case 'order_returned':
    case 'login_qr':
      return 'bg-warn'
    case 'order_created':
      return 'bg-info'
    case 'commission_paid':
    case 'gomdon_message':
      return 'bg-accent'
    default:
      return 'bg-muted'
  }
}

function formatRelativeTime(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffSec = Math.floor(diffMs / 1000)
    const diffMin = Math.floor(diffSec / 60)
    const diffHours = Math.floor(diffMin / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffSec < 60) return 'Vừa xong'
    if (diffMin < 60) return `${diffMin} phút trước`
    if (diffHours < 24) return `${diffHours} giờ trước`
    if (diffDays === 1) return 'Hôm qua'
    if (diffDays < 7) return `${diffDays} ngày trước`
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
  } catch {
    return ''
  }
}

/** Nút chuông + bảng thả xuống. Nguồn dữ liệu do nơi dùng quyết (API đơn vị hoặc hộp thư quản trị khu /npp). */
export function BellShell({
  open,
  onOpenChange,
  unreadCount,
  items,
  loading,
  onItemClick,
  onMarkAll,
  markAllPending,
  footer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  unreadCount: number
  items: BellItem[] | undefined
  loading: boolean
  onItemClick: (item: BellItem) => void
  onMarkAll: () => void
  markAllPending: boolean
  footer?: ReactNode
}) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Đóng popover khi click ra ngoài
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onOpenChange(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onOpenChange])

  return (
    <div className="relative" ref={menuRef}>
      {/* ── Nút chuông thông báo ─────────────────────────────────── */}
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-label="Thông báo"
        aria-expanded={open}
        className={cn(
          // size-11 = 44px, ngưỡng tối thiểu cho mục tiêu chạm. Bản cũ chỉ
          // có `p-2` nên nút ra 36×36 ở MỌI khổ, kể cả điện thoại.
          'relative flex size-11 items-center justify-center desktop:size-10 rounded-lg text-inkSoft hover:text-accent hover:bg-accent/5 transition-colors focus:outline-none',
          open && 'text-accent bg-accent/10'
        )}
      >
        <Bell className="size-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 flex items-center justify-center min-w-4 h-4 px-1 text-xs font-bold text-white bg-danger rounded-full ring-2 ring-raise animate-in zoom-in-50">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Dropdown Popover ──────────────────────────────────────── */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-raise rounded-xl border border-rule shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-rule bg-paperSoft/50">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-ink">Thông báo</h3>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-xs font-semibold bg-danger/10 text-danger rounded-full">
                  {unreadCount} mới
                </span>
              )}
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                onClick={onMarkAll}
                disabled={markAllPending}
                className="text-xs text-muted hover:text-accent flex items-center gap-1 transition-colors disabled:opacity-50"
                title="Đánh dấu tất cả đã đọc"
              >
                {markAllPending ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Check className="size-3" />
                )}
                <span>Đã đọc tất cả</span>
              </button>
            )}
          </div>

          {/* List items */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-rule/60">
            {loading ? (
              <div className="py-12 flex flex-col items-center justify-center text-muted gap-2">
                <Loader2 className="size-5 animate-spin text-accent" />
                <span className="text-xs">Đang tải thông báo...</span>
              </div>
            ) : items && items.length > 0 ? (
              items.map((item) => {
                const tone = getNotificationTone(item)
                const isAnnouncement = item.type === 'announcement'
                const sender = isAnnouncement && typeof item.data?.sender === 'string' ? item.data.sender : null
                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => onItemClick(item)}
                    className={cn(
                      'flex w-full items-start gap-3 p-3.5 text-left hover:bg-paperSoft cursor-pointer transition-colors relative group',
                      !item.isRead && 'bg-accent/5'
                    )}
                  >
                    <span className={cn('mt-2 size-2 shrink-0 rounded-full', tone)} aria-hidden />

                    {/* Content */}
                    <span className="block min-w-0 flex-1">
                      {isAnnouncement && (
                        <span className="mb-1 flex min-w-0 items-center gap-1.5">
                          <KindBadge kind={announceKind(item.data?.kind)} className="shrink-0" />
                          {sender && <span className="truncate text-xs text-muted">{sender}</span>}
                        </span>
                      )}
                      <span className="flex items-baseline justify-between gap-1">
                        <span className={cn('text-xs font-medium text-ink truncate', !item.isRead && 'font-semibold')}>
                          {item.title}
                        </span>
                        <span className="text-xs text-muted shrink-0">
                          {formatRelativeTime(item.createdAt)}
                        </span>
                      </span>
                      <span className="text-xs text-muted line-clamp-2 mt-0.5 leading-relaxed">
                        {item.message}
                      </span>
                    </span>

                    {/* Unread indicator dot */}
                    {!item.isRead && (
                      <span className="size-2 rounded-full bg-accent shrink-0 mt-2 self-start" />
                    )}
                  </button>
                )
              })
            ) : (
              <div className="py-12 px-4 text-center">
                <div className="size-10 rounded-full bg-paperSoft flex items-center justify-center mx-auto mb-2 text-muted">
                  <Bell className="size-5" />
                </div>
                <p className="text-sm font-medium text-ink">Không có thông báo mới</p>
              </div>
            )}
          </div>

          {footer && <div className="p-2.5 border-t border-rule bg-paperSoft/30 text-center">{footer}</div>}
        </div>
      )}
    </div>
  )
}

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const [reading, setReading] = useState<AnnouncementContent | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // 1. Query đếm số lượng chưa đọc (poll nhẹ mỗi 30s)
  const { data: countData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () =>
      get<{ unreadCount: number }>('/notifications/unread-count').catch(() => ({ unreadCount: 0 })),
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
  const unreadCount = countData?.unreadCount ?? 0

  // Đơn vị vừa được nâng cấp (docs/KE_HOACH_NANG_CAP_DON_VI.md §3.8): có thông báo unit.type_changed của đơn vị đang
  // dùng mà giao diện còn loại cũ → làm mới phiên (token + danh sách đơn vị mang loại mới), menu đổi theo ngay.
  useEffect(() => {
    if (!unreadCount) return
    let stop = false
    get<{ items: NotificationItem[] }>('/notifications?limit=8')
      .then(async ({ items }) => {
        const s = getAuthState()
        const active = s.shops.find((x) => x.shopId === s.activeShopId)
        const hit = items.find((n) => !n.isRead && n.data?.kind === 'unit.type_changed' && n.data?.shopId === s.activeShopId && n.data?.toType !== active?.tenantType)
        if (!hit || stop) return
        if (await tryRestoreSession()) toast.success(hit.title, { description: 'Giao diện đã chuyển sang chức năng mới.' })
      })
      .catch(() => {})
    return () => {
      stop = true
    }
  }, [unreadCount])

  // 2. Query lấy 8 thông báo mới nhất khi mở dropdown
  const { data: listData, isLoading: isListLoading } = useQuery({
    queryKey: ['notifications-recent'],
    queryFn: () =>
      get<{ items: NotificationItem[] }>('/notifications?limit=8').catch(() => ({ items: [] })),
    enabled: isOpen,
    staleTime: 10_000,
  })

  // 3. Mutation đánh dấu tất cả đã đọc
  const markAllMutation = useMutation({
    mutationFn: () => post('/notifications/read-all'),
    onSuccess: () => refreshNotifications(queryClient),
  })

  // 4. Mutation đánh dấu 1 item đã đọc
  const markReadMutation = useMutation({
    mutationFn: (id: string) => post(`/notifications/${id}/read`),
    onSuccess: () => refreshNotifications(queryClient),
  })

  const handleItemClick = (item: BellItem) => {
    if (!item.isRead) {
      markReadMutation.mutate(item.id)
    }
    setIsOpen(false)
    // Thông báo cấp trên: mở khung đọc đầy đủ (link, nếu có, nằm trong khung)
    if (item.type === 'announcement') {
      setReading(toAnnouncement(item))
      return
    }
    if (item.data?.url) {
      navigate(item.data.url)
    }
  }

  return (
    <>
      <BellShell
        open={isOpen}
        onOpenChange={setIsOpen}
        unreadCount={unreadCount}
        items={listData?.items}
        loading={isListLoading}
        onItemClick={handleItemClick}
        onMarkAll={() => markAllMutation.mutate()}
        markAllPending={markAllMutation.isPending}
        footer={
          <Link
            to="/notifications"
            onClick={() => setIsOpen(false)}
            className="text-xs font-medium text-accent hover:underline inline-flex items-center gap-1 py-1"
          >
            <span>Xem tất cả</span>
          </Link>
        }
      />
      <AnnouncementSheet a={reading} onClose={() => setReading(null)} />
    </>
  )
}
